import { CacheProvider } from '@emotion/react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import availableThemes from '../theme/available-themes';
import GlobalAppContext from './components/custom-controls/local-selector/global-app-context';
import GlobalAppContextProps from './components/custom-controls/local-selector/global-app-context-props';
import SupportedLocales from './components/custom-controls/local-selector/supported-locales-type';
import SnackbarWrapper from './components/custom-controls/snackbar-wrapper/snackbar-wrapper';
import AvailableThemes from './components/custom-controls/theme-selector/available-themes-type';
import LoadingComponent from './components/util-components/loading-component/loading-component';
import { localeKey, themeKey } from './constants';
import ComponentLoadedProvider from './contexts/component-loaded/component-loaded-provider';
import TaskClipboardProvider from './contexts/task-clipboard/task-clipboard-provider';
import {
	ClearTenantID,
	GetLocale,
	GetTenantID,
	GetTheme,
	SetTenantID,
} from './functions/session-functions';
import translator from './localization/custom-localizations';
import locales from './localization/mui-localizations';
import privateRoutes from './routes/private-routes';
import publicRoutes from './routes/public-routes';
import createEmotionCache from './utils/create-emotion-cache';
import QueryClientWrapper from './utils/create-query-client';

//https://mui.com/material-ui/getting-started/overview/

export default function App() {
	useEffect(() => {
		async function loadTranslator() {
			await translator;
		}
		loadTranslator();
	}, []);

	const emotionCache = useMemo(() => createEmotionCache(), []);
	const [theme, setTheme] = useState<AvailableThemes>(GetTheme());
	const [locale, setLocale] = useState<SupportedLocales>(GetLocale());
	const [tenant, setTenant] = useState<string | undefined>(GetTenantID() ?? undefined);

	useEffect(() => {
		if (locale) {
			sessionStorage.setItem(localeKey, locale);
		}
	}, [locale]);

	useEffect(() => {
		if (theme) {
			sessionStorage.setItem(themeKey, theme);
		}
	}, [theme]);

	useEffect(() => {
		if (tenant) {
			SetTenantID(tenant);
		} else {
			ClearTenantID();
		}
	}, [tenant]);

	const { i18n } = useTranslation();
	const themeWithLocale = useMemo(() => {
		if (locale && theme) {
			if (i18n?.changeLanguage) {
				i18n.changeLanguage(locale.substring(0, 2));
			}
			return createTheme(availableThemes[theme], locales[locale]);
		}
		return createTheme(availableThemes['defaultDarkTheme'], locales['enUS']);
	}, [locale, theme, i18n]);

	const router = useMemo(() => createBrowserRouter([...privateRoutes, ...publicRoutes]), []);
	const [isEditView, setIsEditView] = useState(false);

	const globalAppContextProps: GlobalAppContextProps = useMemo(() => {
		return {
			theme: theme,
			setTheme: setTheme,
			local: locale,
			setLocal: setLocale,
			tenant: tenant,
			setTenant: setTenant,
			isEditView: isEditView,
			setIsEditView: setIsEditView,
		};
	}, [theme, locale, tenant, isEditView]);

	return (
		<GlobalAppContext.Provider value={globalAppContextProps}>
			<QueryClientProvider client={QueryClientWrapper.queryClient}>
				<ReactQueryDevtools initialIsOpen={false} />
				<ThemeProvider theme={themeWithLocale}>
					<ComponentLoadedProvider>
						<CacheProvider value={emotionCache}>
							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<CssBaseline />
								<Suspense fallback={<LoadingComponent />}>
									<TaskClipboardProvider>
										<RouterProvider router={router} />
									</TaskClipboardProvider>
								</Suspense>
								<SnackbarWrapper />
							</LocalizationProvider>
						</CacheProvider>
					</ComponentLoadedProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</GlobalAppContext.Provider>
	);
}
